import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardShell from '../../components/DashboardShell';
import TeacherSideBar from './TeacherSideBar';
import TeacherClassDetails from './TeacherClassDetails';
import TeacherComplain from './TeacherComplain';
import StaffHomePage from './StaffHomePage';
import TeacherProfile from './TeacherProfile';
import AppSettingsPage from '../AppSettingsPage';
import TeacherViewStudent from './TeacherViewStudent';
import StudentAttendance from '../admin/studentRelated/StudentAttendance';
import StudentExamMarks from '../admin/studentRelated/StudentExamMarks';
import TeacherClassResults from './TeacherClassResults';
import TeacherLessonPlans from './TeacherLessonPlans';
import TeacherAssignments from './TeacherAssignments';
import TeacherLeave from './TeacherLeave';
import TeacherResources from './TeacherResources';
import TeacherMarksEntry from './TeacherMarksEntry';
import TeacherEnrollStudent from './TeacherEnrollStudent';
import TeacherFormTeacherResults from './TeacherFormTeacherResults';
import TeacherMyClass from './TeacherMyClass';
import TeacherMyClassStudents from './TeacherMyClassStudents';
import TeacherDisciplinary from './TeacherDisciplinary';
import StudentReportCard from '../admin/examRelated/StudentReportCard';
import TeacherAttendanceEntry from './TeacherAttendanceEntry';
import TeacherSalary from './TeacherSalary';
import SchemeOfWork from './SchemeOfWork';
import SchemeDetail from './SchemeDetail';
import TeacherProfileGate from '../../components/TeacherProfileGate';
import TeacherOfflineBootstrap from '../../components/offline/TeacherOfflineBootstrap';
import HomeroomTeacherOnly from '../../components/HomeroomTeacherOnly';
import { useSelector } from 'react-redux';
import {
    adminLibraryManagedByLibrarian,
    getStaffRoleFromUser,
    isBursar,
    isLibrarianUser,
    isTeachingStaff,
} from '../../utils/staffRoles';
import TeacherSubjectSelector from '../../components/TeacherSubjectSelector';
import { useRefreshTeacherAssignments } from '../../hooks/useRefreshTeacherAssignments';
import FeeStructureManagement from '../admin/feeRelated/FeeStructureManagement';
import FeeCollection from '../admin/feeRelated/FeeCollection';
import FeePaymentSubmissions from '../admin/feeRelated/FeePaymentSubmissions';
import FeeRecordDetail from '../admin/feeRelated/FeeRecordDetail';
import FeeDefaulters from '../admin/feeRelated/FeeDefaulters';
import FeeAidManagement from '../admin/feeRelated/FeeAidManagement';
import FinanceReports from '../admin/feeRelated/FinanceReports';
import ExpenseLog from '../admin/expenseRelated/ExpenseLog';
import ExpenseDetail from '../admin/expenseRelated/ExpenseDetail';
import TermBudgetManagement from '../admin/expenseRelated/TermBudgetManagement';
import ExpenseSummary from '../admin/expenseRelated/ExpenseSummary';
import LibraryManagement from '../admin/libraryRelated/LibraryManagement';
import TeacherTimetablePage from './TeacherTimetablePage';
import FormTeacherTermSubjects from './FormTeacherTermSubjects';
import FormTeacherClassResources from './FormTeacherClassResources';
import TeacherMySubjects from './TeacherMySubjects';
import TeacherMaterialRequests from './TeacherMaterialRequests';
import TeacherAcademicCalendar from './TeacherAcademicCalendar';
import StaffCurrentTermIndicator from '../../components/StaffCurrentTermIndicator';
import TeacherManebPage from './TeacherManebPage';
import TeacherAnalyticsPage from './TeacherAnalyticsPage';
import CaAssessmentManagement from '../admin/examRelated/CaAssessmentManagement';

function BursarFinanceOnly({ children }) {
    const { currentUser } = useSelector((state) => state.user);
    if (!isBursar(getStaffRoleFromUser(currentUser))) {
        return <Navigate to="/Teacher/dashboard" replace />;
    }
    return children;
}

function StaffLibraryRoute() {
    const { currentUser } = useSelector((state) => state.user);
    const { teachersList } = useSelector((state) => state.teacher);
    const staffRole = getStaffRoleFromUser(currentUser);

    if (isLibrarianUser(currentUser)) {
        return <LibraryManagement canManageInventory />;
    }

    const librarianAppointed = adminLibraryManagedByLibrarian(currentUser, teachersList, {
        requireProfileCompleted: true,
    });
    if (librarianAppointed) {
        return <Navigate to="/Teacher/dashboard" replace />;
    }

    if (isTeachingStaff(staffRole) && !isBursar(staffRole)) {
        return <LibraryManagement canManageInventory={false} />;
    }

    return <Navigate to="/Teacher/dashboard" replace />;
}

function TeacherDashboard() {
    useRefreshTeacherAssignments();

    return (
        <DashboardShell
            title="Staff portal"
            sidebar={
                <>
                    <TeacherSubjectSelector />
                    <TeacherSideBar />
                </>
            }
            defaultDesktopOpen
            headerExtra={<StaffCurrentTermIndicator />}
        >
            <TeacherProfileGate>
                <TeacherOfflineBootstrap />
                <Routes>
                    <Route index element={<StaffHomePage />} />
                    <Route path="dashboard" element={<StaffHomePage />} />
                    <Route path="profile" element={<TeacherProfile />} />
                    <Route path="app-settings" element={<AppSettingsPage />} />
                    <Route path="complain" element={<TeacherComplain />} />
                    <Route path="class" element={<TeacherClassDetails />} />
                    <Route path="my-class" element={<TeacherMyClass />} />
                    <Route
                        path="my-class/students"
                        element={
                            <HomeroomTeacherOnly requireClass>
                                <TeacherMyClassStudents />
                            </HomeroomTeacherOnly>
                        }
                    />
                    <Route
                        path="enroll-student"
                        element={
                            <HomeroomTeacherOnly requireClass>
                                <TeacherEnrollStudent />
                            </HomeroomTeacherOnly>
                        }
                    />
                    <Route
                        path="form-teacher-results"
                        element={
                            <HomeroomTeacherOnly requireClass>
                                <TeacherFormTeacherResults />
                            </HomeroomTeacherOnly>
                        }
                    />
                    <Route
                        path="disciplinary"
                        element={
                            <HomeroomTeacherOnly requireClass>
                                <TeacherDisciplinary />
                            </HomeroomTeacherOnly>
                        }
                    />
                    <Route path="report-card/:studentId" element={<StudentReportCard basePath="/Teacher" />} />
                    <Route path="results" element={<TeacherClassResults />} />
                    <Route path="marks-entry" element={<TeacherMarksEntry />} />
                    <Route path="ca-assessment" element={<CaAssessmentManagement teacherMode />} />
                    <Route path="attendance-entry" element={<TeacherAttendanceEntry />} />
                    <Route path="schemes" element={<SchemeOfWork />} />
                    <Route path="schemes/:schemeId" element={<SchemeDetail />} />
                    <Route path="lessons" element={<TeacherLessonPlans />} />
                    <Route path="assignments" element={<TeacherAssignments />} />
                    <Route path="leave" element={<TeacherLeave />} />
                    <Route path="salary" element={<TeacherSalary />} />
                    <Route path="resources" element={<TeacherResources />} />
                    <Route path="class/student/:id" element={<TeacherViewStudent />} />
                    <Route
                        path="class/student/attendance/:studentID/:subjectID"
                        element={<Navigate to="/Teacher/attendance-entry" replace />}
                    />
                    <Route
                        path="class/student/marks/:studentID/:subjectID"
                        element={<Navigate to="/Teacher/marks-entry" replace />}
                    />
                    <Route path="class/student/attendance/:id" element={<StudentAttendance />} />
                    <Route path="class/student/marks/:id" element={<StudentExamMarks situation="Student" />} />

                    <Route path="fees/structures" element={<BursarFinanceOnly><FeeStructureManagement /></BursarFinanceOnly>} />
                    <Route path="fees/collection" element={<BursarFinanceOnly><FeeCollection /></BursarFinanceOnly>} />
                    <Route path="fees/payment-submissions" element={<BursarFinanceOnly><FeePaymentSubmissions /></BursarFinanceOnly>} />
                    <Route path="fees/record/:id" element={<BursarFinanceOnly><FeeRecordDetail /></BursarFinanceOnly>} />
                    <Route path="fees/defaulters" element={<BursarFinanceOnly><FeeDefaulters /></BursarFinanceOnly>} />
                    <Route path="fees/aids" element={<BursarFinanceOnly><FeeAidManagement /></BursarFinanceOnly>} />
                    <Route path="fees/reports" element={<BursarFinanceOnly><FinanceReports /></BursarFinanceOnly>} />
                    <Route path="expenses/log" element={<BursarFinanceOnly><ExpenseLog /></BursarFinanceOnly>} />
                    <Route path="expenses/detail/:id" element={<BursarFinanceOnly><ExpenseDetail /></BursarFinanceOnly>} />
                    <Route path="expenses/budgets" element={<BursarFinanceOnly><TermBudgetManagement /></BursarFinanceOnly>} />
                    <Route path="expenses/summary" element={<BursarFinanceOnly><ExpenseSummary /></BursarFinanceOnly>} />

                    <Route path="library" element={<StaffLibraryRoute />} />
                    <Route path="timetable" element={<TeacherTimetablePage />} />
                    <Route path="my-class/subjects" element={<HomeroomTeacherOnly requireClass><FormTeacherTermSubjects /></HomeroomTeacherOnly>} />
                    <Route path="my-class/resources" element={<HomeroomTeacherOnly requireClass><FormTeacherClassResources /></HomeroomTeacherOnly>} />
                    <Route path="my-subjects" element={<TeacherMySubjects />} />
                    <Route path="material-requests" element={<TeacherMaterialRequests />} />
                    <Route path="academic-calendar" element={<TeacherAcademicCalendar />} />
                    <Route path="analytics" element={<TeacherAnalyticsPage />} />
                    <Route path="maneb" element={<TeacherManebPage />} />

                    <Route path="*" element={<Navigate to="dashboard" replace />} />
                </Routes>
            </TeacherProfileGate>
        </DashboardShell>
    );
}

export default TeacherDashboard;
